/**
 * Single source of truth for "does this hire row match the search box?".
 *
 * Owner, 2026-09-13: *"on https://crewforge.co.uk/department/hires we have
 * Kaban, gannt and calandar views. Can we add a search box on the same line
 * before Kaban button a bit like we have on Dept orders. This new search box
 * should filter out the hires"*. `pages/DepartmentHires.js` is the only caller
 * today; it fetches `/api/hires` ONCE and filters the array in memory, so this
 * is a pure predicate with no request behind it (declared CLIENT_FILTER in
 * `hooks/useSearchRequest.staleGuardManifest.test.js`).
 *
 * ── THE RULE IS SHARED, NOT COPIED ──────────────────────────────────────────
 * `utils/textSearchMatch.js` owns the two-tier loop and the identifier fold;
 * this file supplies only the hire row's FIELD LIST and TOKENS. That module was
 * extracted out of `utils/orderSearch.js` on 2026-09-13 precisely because this
 * predicate would otherwise have been a 0.95-similarity copy of it —
 * `check-duplication` blocked the commit that tried.
 *
 * Prose fields (description, supplier, requested-by …) match as a
 * case-insensitive SUBSTRING; the order's DISPLAY NUMBER matches EXACTLY over a
 * fold that ignores separators, case and leading zeros.
 *
 * 🔑 **`order_number` IS NOT IN THE SUBSTRING LIST AND THAT IS THE WHOLE POINT.**
 * The text loop asks "does the FIELD contain the QUERY", which is wrong in both
 * directions for a bare integer: the stored `4` does not contain the displayed
 * `ORD-004`, and the query `4` would match `order_number` 14, 40 and 400. With
 * per-department numbering small integers are the common case, so that
 * over-match is not a corner case. `foldIdentifier` is IMPORTED from
 * `textSearchMatch.js` rather than copied — a second private copy of that fold
 * is exactly the drift `orderSearch.js` was written to end.
 *
 * 🪤 **THE HIRE ROW CARRIES `po_number` (2026-09-28, for the Gantt), BUT SEARCH
 * DELIBERATELY DOES NOT MATCH IT.** Matching it is a product change, not a gap —
 * do not add a PO clause on the strength of `orderSearch.js` having one. If that is
 * ever ruled, add it to `hireIdentifierTokens` below, not to the text list.
 *
 *   if (hireMatchesSearch(item, query)) {  ... include in the filtered list  }
 *
 * Empty / whitespace-only queries match every row.
 */

import { formatOrderNumber } from './orderTitle';
import { foldIdentifier, matchesTextSearch } from './textSearchMatch';
import { HIRE_STATUS_LABELS } from '../constants/colors';

/**
 * The prose tier. Every entry is a column `GET /api/hires` actually selects
 * (`server/routes/hires.js`) — READ from that SELECT, not guessed:
 *
 *   oi.description, oi.part_number, oi.hire_status,
 *   o.reference_number, o.requested_by, o.supplier_name, o.department,
 *   o.status AS order_status, p.company_name, p.production_name
 *
 * 🪤 `GET /api/hires/calendar` selects a STRICT SUBSET of these (no
 * `part_number`, no `company_name`, no `order_status`). That costs nothing —
 * every read below is null-safe — but it is why the page filters calendar
 * EVENTS by membership in the filtered items' ids rather than re-running this
 * predicate over the event array: the two shapes would answer differently for
 * the same hire.
 *
 * 🔴 `reference_number` IS SEARCHABLE AND IS DISPLAYED NOWHERE — the same
 * standing ruling as `orderSearch.js`'s entry for it. Paperwork already sent to
 * a supplier carries the old string, so being findable is not being shown.
 */
const SEARCHABLE_FIELDS = [
  'description',
  'part_number',
  'supplier_name',
  'requested_by',
  'reference_number',
  'department',
  'production_name',
  'company_name',
  'hire_status',
  'order_status',
];

/**
 * Every folded string that names THIS hire's order. Exact membership, never
 * substring — see the tier note above.
 *
 * 🪤 `formatOrderNumber(null)` is `null`, so a row with no order number
 * contributes no `ord…` token and a search for `ord` cannot reach it through
 * this tier.
 */
function hireIdentifierTokens(item) {
  const tokens = new Set();

  const formatted = formatOrderNumber(item.order_number);
  if (formatted) {
    tokens.add(foldIdentifier(formatted));        // ORD-004 → ord4
    tokens.add(foldIdentifier(item.order_number)); // 4 → 4
  }

  tokens.delete('');
  return tokens;
}

/**
 * 🔑 THE STATUS THE USER CAN SEE, not only the one the database stores. A
 * `pending` hire is labelled **Upcoming** on every surface of this page
 * (`constants/colors.js` `HIRE_STATUS_LABELS`, and the Kanban column header),
 * and the word `pending` appears nowhere on screen — so a search for "upcoming"
 * matching nothing would be the box lying about a row the user is looking at.
 * The raw value stays in SEARCHABLE_FIELDS as well: both spellings are honest
 * and neither is a superset of the other.
 *
 * Returned as a one-element ARRAY because that is `extraValues`' contract in
 * `textSearchMatch.js` — derived text, matched as a substring beside `fields`.
 */
function hireStatusLabel(item) {
  return [HIRE_STATUS_LABELS[item.hire_status] || null];
}

const HIRE_SEARCH_SPEC = {
  fields: SEARCHABLE_FIELDS,
  identifierTokens: hireIdentifierTokens,
  extraValues: hireStatusLabel,
};

export function hireMatchesSearch(item, searchTerm) {
  return matchesTextSearch(item, searchTerm, HIRE_SEARCH_SPEC);
}
